package com.hyperether.goodjob.scenes.register

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.repository.Mapper
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.remote.auth.AppleLoginRequest
import com.hyperether.goodjob.repository.remote.auth.GoogleLoginRequest
import com.hyperether.goodjob.repository.remote.auth.MicrosoftLoginRequest
import com.hyperether.goodjob.repository.remote.model.ForgotPasswordRequest
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.util.JWTUtils
import com.hyperether.goodjob.util.Logger
import com.hyperether.resources.stringResourcePlain
import com.mmk.kmpnotifier.notification.NotifierManager
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import com.hyperether.auth.Platform
import com.hyperether.auth.apple.AppleSignInResult
import com.hyperether.auth.google.GoogleSignInResult
import com.hyperether.auth.microsoft.MicrosoftSignInResult
import org.jetbrains.compose.resources.getString

class RegisterViewModel(
    val repository: Repository,
) : ViewModel() {

    private val TAG: String = "RegisterViewModel"
    val errorText: MutableState<String> = mutableStateOf("")
    val errorInternalCode: MutableState<Int?> = mutableStateOf(null)
    val emailInput: MutableState<String> = mutableStateOf("")
    val showSendResetLinkMessage: MutableState<Boolean> = mutableStateOf(false)
    val showPopUpError: MutableState<Boolean> = mutableStateOf(false)
    val showPopUpSuccess: MutableState<Boolean> = mutableStateOf(false)
    val showLoader: MutableState<Boolean> = mutableStateOf(false)
    val showForgotPasswordView = mutableStateOf(false)

    fun register(
        name: String,
        email: String,
        password: String,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        viewModelScope.launch(Dispatchers.Default) {
            when (val result =
                repository.register(name, email, password)) {
                is Resource.Success -> {
                    repository.saveEmail(email)
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        }
    }

    fun forgotPassword() {
        viewModelScope.launch {
            when (val result = repository.forgotPassword(ForgotPasswordRequest(emailInput.value))) {
                is Resource.Success -> {
                    result.text?.let { Logger.d(TAG, it) }
                    showSendResetLinkMessage.value = true
                    repository.saveEmail(emailInput.value)
                    emailInput.value = ""
                }

                is Resource.Error -> {
                    showPopUpError.value = true
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    result.text?.let { Logger.d(TAG, it) }
                }
            }
        }
    }

    fun login(email: String, password: String, onSuccess: () -> Unit, onError: (String) -> Unit) {
        viewModelScope.launch(Dispatchers.Main) {

            when (val result =
                repository.login(email, password)) {
                is Resource.Success -> {
                    // TODO check for place for this method
                    //getAllContacts()
                    //getAllUsers()
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    if (result.errorInternalCode != null) {
                        errorInternalCode.value = result.errorInternalCode
                    }
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        }
    }

    fun resendConfirmationEmail(email: String, onSuccess: () -> Unit, onError: (String) -> Unit) {
        viewModelScope.launch {
            when (val result =
                repository.resendConfirmationEmail(email)) {
                is Resource.Success -> {
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    if (result.errorInternalCode != null) {
                        errorInternalCode.value = result.errorInternalCode
                    }
                    onError(errorText.value)
                }
            }
        }
    }

    fun getUser(): User? {
        return repository.getUser()
    }

    fun isEmployee(): Boolean {
        return repository.getUser()?.role.equals(Role.employee.name, ignoreCase = true)
    }

    fun isManager(): Boolean {
        return repository.getUser()?.role.equals(Role.manager.name, ignoreCase = true)
    }

    fun isSuperAdmin(): Boolean {
        return repository.getUser()?.role.equals(Role.superAdmin.name, ignoreCase = true)
    }

    suspend fun updatePushToken() {
        val pushToken = NotifierManager.getPushNotifier().getToken() ?: repository.getFCMPushToken()
        repository.getUser()?.let {
            it._id?.let { userId ->
                Logger.d("FCM", "update token: $pushToken for ${it.email}")
                it.pushToken = pushToken
                repository.updateUser(userId, Mapper().toUserRequest(it))
                Logger.d("FCM", "token updated")
            }
        }
    }

    fun loginWithGoogle(
        googleResult: GoogleSignInResult?,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        val tokenData = JWTUtils.extractClaims(googleResult?.idToken)
        val userId = googleResult?.userId ?: tokenData?.sub
        val email = googleResult?.email ?: tokenData?.email
        val name = googleResult?.fullName ?: tokenData?.name

        val request = GoogleLoginRequest(
            userId = userId,
            email = email,
            name = name
        )

        viewModelScope.launch(Dispatchers.Main) {
            when (val result =
                repository.loginGoogle(request)) {
                is Resource.Success -> {
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    if (result.errorInternalCode != null) {
                        errorInternalCode.value = result.errorInternalCode
                    }
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        }
    }

    fun loginWithApple(
        appleResult: AppleSignInResult?,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {

        val source = if (appleResult?.platform == Platform.IOS) {
            "ios"
        } else {
            "web"
        }
        val request = AppleLoginRequest(
            code = appleResult?.authCode,
            source = source
        )

        viewModelScope.launch(Dispatchers.Main) {
            when (val result =
                repository.loginApple(request)) {
                is Resource.Success -> {
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    if (result.errorInternalCode != null) {
                        errorInternalCode.value = result.errorInternalCode
                    }
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        }
    }

    fun loginWithMicrosoft(
        microsoftResult: MicrosoftSignInResult?,
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        val tokenData = JWTUtils.extractClaims(microsoftResult?.token)
        val tenantId = microsoftResult?.tenantId ?: tokenData?.sub
        val email = microsoftResult?.email ?: tokenData?.email
        val name = tokenData?.name

        val request = MicrosoftLoginRequest(
            tenantId = tenantId,
            email = email,
            name = name
        )

        viewModelScope.launch(Dispatchers.Main) {
            when (val result =
                repository.loginMicrosoft(request)) {
                is Resource.Success -> {
                    onSuccess()
                }

                is Resource.Error -> {
                    errorText.value = result.text ?: stringResourcePlain(Res.string.unknown_error)
                    if (result.errorInternalCode != null) {
                        errorInternalCode.value = result.errorInternalCode
                    }
                    onError(result.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        }
    }
}